import * as Sentry from "@sentry/nextjs";
import { makeAutoObservable, runInAction } from "mobx";

import { duplicateDocument, duplicateFolder, getApiErrorMessage, getTeam } from "@/api";
import { docPageUrl } from "@/urls";
import { getLastDocIdForTeam } from "@/utils/useLastDocId";

import type { TeamModel } from "@/models/TeamModel";
import type { RootStore } from "@/store/RootStore";

export class ForkDialogStore {
  rootStore: RootStore;

  isOpen = false;

  docUidToFork?: string;

  folderUidToFork?: string;

  forkError?: string;

  status: "idle" | "loading" | "error" = "idle";

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  open({
    docUidToFork,
    folderUidToFork,
  }:
    | { docUidToFork: string; folderUidToFork?: never }
    | { docUidToFork?: never; folderUidToFork: string }) {
    this.isOpen = true;
    this.docUidToFork = docUidToFork;
    this.folderUidToFork = folderUidToFork;
    this.forkError = undefined;
  }

  close() {
    this.isOpen = false;
    this.docUidToFork = undefined;
    this.folderUidToFork = undefined;
    this.forkError = undefined;
    this.status = "idle";
  }

  handleForkButtonClick = (
    args:
      | { docUidToFork: string; folderUidToFork?: never }
      | { docUidToFork?: never; folderUidToFork: string },
  ) => {
    if (!this.rootStore.auth.isLoggedIn) {
      this.rootStore.dialog.loginDialog.open();
      return;
    }

    if (this.rootStore.teams.userTeams.length === 1) {
      if (args.docUidToFork) {
        return this.forkDocument(args.docUidToFork, this.rootStore.teams.userTeams[0]);
      }
      if (args.folderUidToFork) {
        return this.forkFolder(args.folderUidToFork, this.rootStore.teams.userTeams[0]);
      }
      throw new Error("No document or folder to fork");
    } else {
      this.open(args);
    }
  };

  forkDocument = async (docUidToFork: string, teamToForkTo: TeamModel) => {
    runInAction(() => {
      this.status = "loading";
    });
    try {
      const isSameTeam = teamToForkTo.permalink === this.rootStore.teams.currentTeam?.permalink;
      let draftsUid = "";

      if (isSameTeam) {
        draftsUid = this.rootStore.documentTree.drafts?.uid || "";
        const { document } = await duplicateDocument(docUidToFork, {
          parentUid: draftsUid,
          position: 0,
        });
        this.rootStore.documentTree.openFolder(draftsUid);
        return document;
      }

      // TeamModel may not have drafts folder uid if it was created based on TeamShort
      draftsUid = teamToForkTo.drafts?.uid || "";
      if (!draftsUid) {
        const { team } = await getTeam(teamToForkTo.permalink);
        draftsUid = team?.drafts?.uid || "";
      }

      const { document } = await duplicateDocument(docUidToFork, {
        parentUid: draftsUid,
        position: 0,
      });
      this.rootStore.teams.setCurrentTeam(teamToForkTo);

      runInAction(() => {
        this.status = "idle";
      });
      return document;
    } catch (error) {
      runInAction(() => {
        this.status = "error";
      });
      this.forkError = getApiErrorMessage(error);
      Sentry.captureException(error);
      throw error;
    }
  };

  forkFolder = async (folderUidToFork: string, teamToForkTo: TeamModel) => {
    // We don't change status here, because folder duplication can take a while and we don't want to block the UI
    try {
      const isSameTeam = teamToForkTo.permalink === this.rootStore.teams.currentTeam?.permalink;
      let draftsUid = "";

      if (isSameTeam) {
        draftsUid = this.rootStore.documentTree.drafts?.uid || "";
        this.rootStore.documentTree.openFolder(draftsUid);
        const lastDocId = getLastDocIdForTeam(teamToForkTo.uid);
        if (lastDocId) {
          this.rootStore.router.push(docPageUrl({ docId: lastDocId }));
        }
        const { folder } = await duplicateFolder(folderUidToFork, {
          parentUid: draftsUid,
          position: 0,
        });
        return folder;
      }

      draftsUid = teamToForkTo.drafts?.uid || "";
      if (!draftsUid) {
        const { team } = await getTeam(teamToForkTo.permalink);
        draftsUid = team?.drafts?.uid || "";
      }

      this.rootStore.teams.setCurrentTeam(teamToForkTo);
      if (draftsUid) {
        this.rootStore.documentTree.openFolder(draftsUid);
      }
      const lastDocId = getLastDocIdForTeam(teamToForkTo.uid);
      if (lastDocId) {
        this.rootStore.router.push(docPageUrl({ docId: lastDocId }));
      }

      const { folder } = await duplicateFolder(folderUidToFork, {
        parentUid: draftsUid,
        position: 0,
      });
      return folder;
    } catch (error) {
      this.forkError = getApiErrorMessage(error);
      Sentry.captureException(error);
      throw error;
    }
  };
}
